import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialUIModule } from './material-ui/material-ui.module';
import { ToolbarComponent } from './shared/toolbar/toolbar.component';
import { HomeComponent } from './home/home.component';
import { environment } from 'src/environments/environment';
import { API_BASE_URL, CityServiceProxy, OrdersServiceProxy, StateServiceProxy, VetServiceProxy } from './services/service-proxy/service-proxies';
import { ClienteUnicoServiceProxy } from './services/service-proxy/service-proxies';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PetFormComponent } from './pet/pet-form/pet-form.component';
import { PetDetailComponent } from './pet/pet-detail/pet-detail.component';

import { CustomerFormComponent } from './customer/customer-form.component';
import { UiService } from './services/ui.service';
import { MyHttpInterceptor } from './interceptors/httpInterceptor';
import { LoadingIndicatorComponent } from './shared/loading-indicator/loading-indicator.component';
import { AppService } from './services/app.service';
import { PetListComponent } from './pet/pet-list/pet-list.component';
import { CustomerAdressListComponent } from './customer-adress-list/customer-adress-list.component';
import { CustomerAdressFormComponent } from './customer-adress-form/customer-adress-form.component';
import { OrdersListComponent } from './orders-list/orders-list.component';
import { OrderDetailComponent } from './order-detail/order-detail.component';
import { OrdersListItemComponent } from './orders-list/order-list-item/order-list-item.component';
import { SuccessComponent } from './shared/success/success.component';
import { AuthenticationInterceptor } from './interceptors/authentication-interceptor';
import { AuthModule, LogLevel } from 'angular-auth-oidc-client';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { LetterDirective } from './directives/letterDirective';
import { NumberDirective } from './directives/numberDirective';
import { VetExtendedServiceProxy } from './services/service-proxy/service-proxies-vet';
import { HomeBenefitsComponent } from './home/home-benefits/home-benefits.component';
import { ResumenPuntosComponent } from './home/resumen-puntos/resumen-puntos.component';
import { MisMascotasResumenComponent } from './home/mis-mascotas-resumen/mis-mascotas-resumen.component';
import { BeneficiosResumenComponent } from './home/beneficios-resumen/beneficios-resumen.component';
import { UltimasComprasComponent } from './home/ultimas-compras/ultimas-compras.component';
import { HistorialRecienteComponent } from './home/historial-reciente/historial-reciente.component';
import { TopbarComponent } from './shared/topbar/topbar.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { PantallaPendienteComponent } from './shared/pantalla-pendiente/pantalla-pendiente.component';
import { StoresService } from './services/stores.service';
import { PetClinicalService } from './services/pet-clinical.service';
import { PetCardComponent } from './shared/pet-card/pet-card.component';
import { MisMascotasComponent } from './mascotas/mis-mascotas.component';
import { MisBeneficiosComponent } from './beneficios/mis-beneficios.component';
import { PreferenciasComponent } from './preferencias/preferencias.component';
import { HistorialPuntosComponent } from './puntos/historial-puntos.component';
import { TabbarComponent } from './shared/tabbar/tabbar.component';

// Los pipes number y currency de la home formatean con 'es-AR' ("4.820", "$47.300").
// Sin esto tiran "Missing locale data" en runtime. Se registra el locale pero NO se
// cambia LOCALE_ID global, para no alterar el formato del resto de la app.
registerLocaleData(localeEsAr, 'es-AR');

export class CustomLocalStorage {
  private storage: Storage | any;

  constructor() {
    try {
      // Intenta usar localStorage con una prueba simple
      this.storage = localStorage;
      this.storage.setItem('test', 'test');
      this.storage.removeItem('test');
    } catch (e) {
      console.warn('localStorage no está disponible, usando sessionStorage como fallback:', e);
      this.storage = sessionStorage; // Fallback a sessionStorage si localStorage falla
    }
  }

  // Métodos básicos de Storage
  getItem(key: string): string | null {
    return this.storage.getItem(key);
  }

  setItem(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  removeItem(key: string): void {
    this.storage.removeItem(key);
  }

  // Métodos adicionales requeridos por angular-auth-oidc-client
  read(key: string): any {
    const value = this.getItem(key);
    return value ? JSON.parse(value) : null; // Deserializa el valor
  }

  write(key: string, value: any): void {
    this.setItem(key, JSON.stringify(value)); // Serializa el valor
  }

  clear(): void {
    this.storage.clear();
  }

  length(): number {
    return this.storage.length;
  }

  key(index: number): string | null {
    return this.storage.key(index);
  }
}

@NgModule({
  declarations: [
    AppComponent,
    ToolbarComponent,
    LoadingIndicatorComponent,
    SuccessComponent,
    HomeComponent,
    PetFormComponent,
    PetDetailComponent,
    CustomerFormComponent,
    PetListComponent,
    CustomerAdressListComponent,
    CustomerAdressFormComponent,
    OrdersListComponent,
    OrdersListItemComponent,
    OrderDetailComponent,
    UnauthorizedComponent,
    LetterDirective,
    NumberDirective,
    HomeBenefitsComponent,
    ResumenPuntosComponent,
    MisMascotasResumenComponent,
    BeneficiosResumenComponent,
    UltimasComprasComponent,
    HistorialRecienteComponent,
    TopbarComponent,
    SidebarComponent,
    PantallaPendienteComponent,
    TabbarComponent,
    PetCardComponent,
    MisMascotasComponent,
    MisBeneficiosComponent,
    PreferenciasComponent,
    HistorialPuntosComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialUIModule,
    HttpClientModule,
    // 
    AuthModule.forRoot({
      config: {
        authority: getAuthServerUrl(),
        postLoginRoute: '/home',
        forbiddenRoute: '/forbidden',
        unauthorizedRoute: '/unauthorized',
        redirectUrl: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        clientId: 'angular',
        scope: 'openid profile singlecustomer countrycode',
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        logLevel: LogLevel.Warn,
        storage: new CustomLocalStorage() 
      },
    }),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: MyHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
    ClienteUnicoServiceProxy,
    VetServiceProxy,
    VetExtendedServiceProxy,
    OrdersServiceProxy,
    StateServiceProxy,
    CityServiceProxy,
    { 
      provide: API_BASE_URL, 
      useFactory: getRemoteServiceBaseUrl 
    }, 
    UiService,
    StoresService,
    PetClinicalService,
    AppService
    
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



export function getRemoteServiceBaseUrl(): string {
  return environment.singleCustomerUrl;
}

export function getAuthServerUrl(): string {
  return environment.authorityServerUrl;
}



